import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ShieldCheck, Lock, ArrowLeft } from 'lucide-react';
import { PageHeader } from '../PageHeader';
import { AdminPage } from './AdminPage';
import { useApp } from '../store';
import { isGraphConfigured } from '../graph/config';
// Gate that keeps the intranet open to everyone while restricting the Site
// Admin panel to people with the marketing passcode. No login required.
export function AdminGate() {
  const {
    isAdmin
  } = useApp();
  if (isAdmin) return <AdminPage data-id="element-215" data-mp="36cf1h3q1rwvh" />;
  return <AdminUnlock data-id="element-216" data-mp="2seqhdy3t0trh" />;
}
// Chooses the right locked-state view. In Microsoft mode admin is driven by
// Entra group membership (no passcode), so a non-member just sees a request-
// access message. In local mode the marketing passcode form is shown.
function AdminUnlock() {
  if (isGraphConfigured()) return <AdminGroupNotice data-id="element-217" data-mp="1lyo6vdyk16yf" />;
  return <AdminPasscodeForm data-id="element-218" data-mp="1lyxvax3yb620" />;
}
// Microsoft mode: the visitor is signed in but not in the admin group.
function AdminGroupNotice() {
  return <>
      <PageHeader title="Site Administration" subtitle="Restricted to the marketing & communications team." data-id="element-219" data-mp="o65yzkxgiuol" />
      <div className="max-w-md mx-auto mt-12 text-center" data-id="element-220" data-mp="y1zfffear8bn">
        <div className="w-14 h-14 rounded-2xl bg-navy-50 text-navy-400 flex items-center justify-center mx-auto mb-4" data-id="element-221" data-mp="y069k2bics4p">
          <Lock className="w-7 h-7" data-id="element-222" data-mp="172xo4azok40k" />
        </div>
        <h2 className="text-xl font-serif font-bold text-navy-900" data-id="element-223" data-mp="171mkqca5cy9z">
          You don’t have Site Admin access
        </h2>
        <p className="text-sm text-navy-500 mt-2" data-id="element-224" data-mp="123kth42khsnf">
          Site Admin is limited to members of the{' '}
          <span className="font-semibold text-navy-700" data-id="element-225" data-mp="1gutl1iq15o37">
            IndependenceEX-Admins
          </span>{' '}
          group. Ask IT or your marketing lead to add you.
        </p>
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-500 hover:text-navy-700 mt-6" data-id="element-226" data-mp="1ptotl1gjra9o">
          <ArrowLeft className="w-4 h-4" data-id="element-227" data-mp="1svjt03jc83it" /> Back to home
        </Link>
      </div>
    </>;
}
// Local (preview / pre-integration) mode: the shared marketing passcode.
function AdminPasscodeForm() {
  const {
    unlockAdmin
  } = useApp();
  const [code, setCode] = useState('');
  const [error, setError] = useState(false);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const ok = unlockAdmin(code);
    if (!ok) {
      setError(true);
      setCode('');
    }
  };
  return <>
      <PageHeader title="Site Administration" subtitle="Restricted to the marketing & communications team." data-id="element-228" data-mp="2gk30igukcc9u" />
      <div className="max-w-md" data-id="element-229" data-mp="2lk8onn1mjgn6">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="bg-white rounded-2xl border border-navy-100 p-8 text-center" data-id="element-230" data-mp="2lhuwpn28qkju">
          <div className="w-14 h-14 rounded-2xl bg-navy-600 text-gold-400 flex items-center justify-center mx-auto mb-4" data-id="element-231" data-mp="w5av8npyv1vl">
            <ShieldCheck className="w-7 h-7" data-id="element-232" data-mp="39h575g43etrd" />
          </div>
          <h2 className="text-xl font-serif font-bold text-navy-900" data-id="element-233" data-mp="34niuhnxxipvk">
            Enter the marketing access code
          </h2>
          <p className="text-sm text-navy-500 mt-1 mb-6" data-id="element-234" data-mp="30kmiwh6t3u14">
            The intranet is open to everyone, but publishing and configuration
            are limited to the marketing team.
          </p>
          <form onSubmit={submit} className="space-y-3 text-left" data-id="element-235" data-mp="3sbso3c1dwhx9">
            <label className="block" data-id="element-236" data-mp="3odcqke7ofr5l">
              <span className="sr-only" data-id="element-237" data-mp="3oa76xehthhh9">Access code</span>
              <div className="relative" data-id="element-238" data-mp="3jc5i0cxpmnxv">
                <Lock className="w-4 h-4 text-navy-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" data-id="element-239" data-mp="3jf1juccohwrl" />
                <input type="password" autoFocus value={code} onChange={e => {
                setCode(e.target.value);
                setError(false);
              }} placeholder="Access code" className={`w-full pl-9 pr-3 py-2.5 rounded-lg bg-navy-50 border text-sm text-navy-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-gold-400/30 ${error ? 'border-red-400' : 'border-transparent focus:border-gold-400'}`} data-id="element-240" data-mp="27xs1jt5adarv" />
              </div>
            </label>
            {error && <p className="text-sm text-red-600" data-id="element-241" data-mp="efayqp0sc8c8">
                That code isn't right. Check with your marketing lead.
              </p>}
            <button type="submit" className="w-full px-5 py-2.5 text-sm font-bold text-navy-900 bg-gold-500 rounded-lg hover:bg-gold-400 transition-colors" data-id="element-242" data-mp="idifvkz5vbny">
              Unlock Site Admin
            </button>
          </form>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm font-semibold text-navy-500 hover:text-navy-700 mt-5" data-id="element-243" data-mp="2p4pzzgbjkkp8">
            <ArrowLeft className="w-4 h-4" data-id="element-244" data-mp="2ml3eqrkejmnr" /> Back to home
          </Link>
        </motion.div>
      </div>
    </>;
}